import { dictionaryApi } from './dictionaryClient';
import { normalizeBarnaSamuha, type BarnaSamuha } from './barnaSamuhaApi';
import { normalizeAssociatedBarna, type AssociatedBarna } from './pratyaharApi';

export type { AssociatedBarna };

export type BartikPadachhedBarnaSamuhaLink = {
  id: number;
  ashtadhyayiBartikPadachhedId: number;
  barnaSamuhaId: number;
  selectedBarnas: AssociatedBarna[];
  barnaSamuha: BarnaSamuha | null;
};

export type CreateBartikPadachhedBarnaSamuhaLinkBody = {
  ashtadhyayiBartikPadachhedId: number;
  barnaSamuhaId: number;
  selectedBarnas: AssociatedBarna[];
};

export type UpdateBartikPadachhedBarnaSamuhaLinkBody = {
  selectedBarnas?: AssociatedBarna[];
};

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

function num(o: Record<string, unknown>, key: string): number | null {
  const v = o[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

export function normalizeBartikPadachhedBarnaSamuhaLink(raw: unknown): BartikPadachhedBarnaSamuhaLink | null {
  if (!isRecord(raw)) return null;
  const id = num(raw, 'id');
  const ashtadhyayiBartikPadachhedId =
    num(raw, 'ashtadhyayiBartikPadachhedId') ?? num(raw, 'ashtadhyayi_bartik_padachhed_id');
  const barnaSamuhaId = num(raw, 'barnaSamuhaId') ?? num(raw, 'barna_samuha_id');
  if (id == null || ashtadhyayiBartikPadachhedId == null || barnaSamuhaId == null) return null;

  const selectedRaw = raw.selectedBarnas ?? raw.selected_barnas;
  const selectedBarnas = Array.isArray(selectedRaw)
    ? selectedRaw.map(normalizeAssociatedBarna).filter((x): x is AssociatedBarna => x != null)
    : [];

  const barnaSamuhaRaw = raw.barnaSamuha ?? raw.barna_samuha;
  const barnaSamuha = barnaSamuhaRaw ? normalizeBarnaSamuha(barnaSamuhaRaw) : null;

  return {
    id,
    ashtadhyayiBartikPadachhedId,
    barnaSamuhaId,
    selectedBarnas,
    barnaSamuha,
  };
}

function normalizeList(data: unknown): BartikPadachhedBarnaSamuhaLink[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows
    .map(normalizeBartikPadachhedBarnaSamuhaLink)
    .filter((x): x is BartikPadachhedBarnaSamuhaLink => x != null);
}

export async function listBartikPadachhedBarnaSamuhaLinks(filters?: {
  ashtadhyayiBartikPadachhedId?: number;
  barnaSamuhaId?: number;
}): Promise<BartikPadachhedBarnaSamuhaLink[]> {
  const params: Record<string, number> = {};
  if (filters?.ashtadhyayiBartikPadachhedId != null) {
    params.ashtadhyayiBartikPadachhedId = filters.ashtadhyayiBartikPadachhedId;
  }
  if (filters?.barnaSamuhaId != null) {
    params.barnaSamuhaId = filters.barnaSamuhaId;
  }
  const res = await dictionaryApi.get<unknown>('/ashtadhyayi-bartik-padachhed-barna-samuha', {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return normalizeList(res.data);
}

export async function getBartikPadachhedBarnaSamuhaLink(id: number): Promise<BartikPadachhedBarnaSamuhaLink> {
  const res = await dictionaryApi.get<unknown>(`/ashtadhyayi-bartik-padachhed-barna-samuha/${id}`);
  const row = normalizeBartikPadachhedBarnaSamuhaLink(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bartik padachhed-barna samuha link response');
  return row;
}

export async function createBartikPadachhedBarnaSamuhaLink(
  body: CreateBartikPadachhedBarnaSamuhaLinkBody
): Promise<BartikPadachhedBarnaSamuhaLink> {
  const res = await dictionaryApi.post<unknown>('/ashtadhyayi-bartik-padachhed-barna-samuha', body);
  const row = normalizeBartikPadachhedBarnaSamuhaLink(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bartik padachhed-barna samuha link response');
  return row;
}

export async function updateBartikPadachhedBarnaSamuhaLink(
  id: number,
  body: UpdateBartikPadachhedBarnaSamuhaLinkBody
): Promise<BartikPadachhedBarnaSamuhaLink> {
  const res = await dictionaryApi.patch<unknown>(`/ashtadhyayi-bartik-padachhed-barna-samuha/${id}`, body);
  const row = normalizeBartikPadachhedBarnaSamuhaLink(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bartik padachhed-barna samuha link response');
  return row;
}

export async function deleteBartikPadachhedBarnaSamuhaLink(id: number): Promise<void> {
  await dictionaryApi.delete(`/ashtadhyayi-bartik-padachhed-barna-samuha/${id}`);
}
